/**
 * Đọc bảng tính người dùng chọn (.xlsx hoặc .csv) ngay trong trình duyệt → mảng dòng chuỗi.
 *
 * Dùng cho màn nhập danh mục Đại lý/MM: file KHÔNG được gửi lên máy chủ (ngoài phạm vi —
 * CLAUDE.md "không object File / upload"), chỉ các dòng đã đọc được gửi đi dạng JSON để
 * máy chủ kiểm và nhập. Không kéo thư viện: .xlsx là ZIP chứa XML, giải nén bằng
 * `DecompressionStream('deflate-raw')` có sẵn trong trình duyệt (và Node ≥ 21 cho test).
 *
 * Chỉ đọc SHEET ĐẦU TIÊN, chỉ lấy giá trị hiển thị dạng chuỗi — không công thức, không định
 * dạng. Đủ cho một danh mục; không phải bộ đọc Excel tổng quát.
 */

const td = new TextDecoder('utf-8');

// ------------------------------------------------------------------- ZIP
/** Danh sách mục trong ZIP: tên → {method, offset, compressedSize}. Đọc từ central directory. */
function zipEntries(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // End of central directory: chữ ký 0x06054b50, nằm trong 64KB cuối (sau nó là comment).
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('File không phải .xlsx hợp lệ (không đọc được cấu trúc ZIP)');
  const count = view.getUint16(eocd + 10, true);
  let p = view.getUint32(eocd + 16, true);
  const entries = new Map();
  for (let n = 0; n < count; n++) {
    if (view.getUint32(p, true) !== 0x02014b50) throw new Error('File .xlsx bị hỏng (central directory)');
    const method = view.getUint16(p + 10, true);
    const compressedSize = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const offset = view.getUint32(p + 42, true);
    const name = td.decode(bytes.subarray(p + 46, p + 46 + nameLen));
    entries.set(name, { method, compressedSize, offset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return { entries, view };
}

async function inflateRaw(data) {
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readEntry(bytes, zip, name) {
  const e = zip.entries.get(name);
  if (!e) return null;
  // Local header: độ dài tên/extra ở ĐÂY có thể khác central directory — đọc lại.
  const nameLen = zip.view.getUint16(e.offset + 26, true);
  const extraLen = zip.view.getUint16(e.offset + 28, true);
  const start = e.offset + 30 + nameLen + extraLen;
  const data = bytes.subarray(start, start + e.compressedSize);
  if (e.method === 0) return td.decode(data);
  if (e.method === 8) return td.decode(await inflateRaw(data));
  throw new Error(`File .xlsx dùng kiểu nén không hỗ trợ (${e.method})`);
}

// ------------------------------------------------------------------- XML
const unescapeXml = (s) =>
  s
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');

/** Nối mọi `<t>` trong một khối (ô có định dạng chữ nhiều đoạn — rich text — chia thành nhiều `<t>`). */
const textOf = (xml) => [...xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((m) => unescapeXml(m[1])).join('');

/** "AB" → 27 (0-based) */
function columnIndex(ref) {
  const letters = /^[A-Z]+/.exec(ref)?.[0] ?? 'A';
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

async function readXlsx(bytes) {
  const zip = zipEntries(bytes);
  // Sheet đầu tiên theo workbook.xml (thứ tự tab), không đoán tên "sheet1.xml".
  const workbook = (await readEntry(bytes, zip, 'xl/workbook.xml')) ?? '';
  const rels = (await readEntry(bytes, zip, 'xl/_rels/workbook.xml.rels')) ?? '';
  const firstRid = /<sheet\b[^>]*\br:id="([^"]+)"/.exec(workbook)?.[1];
  const target = firstRid ? new RegExp(`<Relationship\\b[^>]*Id="${firstRid}"[^>]*Target="([^"]+)"`).exec(rels)?.[1] : null;
  const sheetPath = target ? (target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`) : 'xl/worksheets/sheet1.xml';
  const sheet = await readEntry(bytes, zip, sheetPath);
  if (!sheet) throw new Error('Không tìm thấy sheet đầu tiên trong file .xlsx');

  const sharedXml = (await readEntry(bytes, zip, 'xl/sharedStrings.xml')) ?? '';
  const shared = [...sharedXml.matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => textOf(m[1]));

  const rows = [];
  for (const rm of sheet.matchAll(/<row\b([^>]*)>([\s\S]*?)<\/row>/g)) {
    const r = Number(/\br="(\d+)"/.exec(rm[1])?.[1] ?? rows.length + 1) - 1;
    const row = [];
    for (const cm of rm[2].matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = cm[1];
      const body = cm[2] ?? '';
      const ref = /\br="([A-Z]+\d+)"/.exec(attrs)?.[1];
      const c = ref ? columnIndex(ref) : row.length;
      const type = /\bt="([^"]+)"/.exec(attrs)?.[1];
      const v = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];
      let value = '';
      if (type === 's') value = shared[Number(v)] ?? '';
      else if (type === 'inlineStr') value = textOf(body);
      else if (v != null) value = unescapeXml(v);
      row[c] = value;
    }
    rows[r] = Array.from(row, (x) => x ?? '');
  }
  return Array.from(rows, (x) => x ?? []);
}

// ------------------------------------------------------------------- CSV
/** CSV theo RFC 4180; dấu phân cách `,` hoặc `;` (Excel tiếng Việt hay lưu bằng `;`) — đoán từ dòng đầu. */
export function parseCsv(text) {
  // BOM của Excel khi lưu "CSV UTF-8". Dựng bằng mã ký tự: BOM gõ thẳng vào nguồn là vô hình.
  const bom = String.fromCharCode(0xfeff);
  const src = text.startsWith(bom) ? text.slice(1) : text;
  const firstLine = src.split(/\r?\n/, 1)[0] ?? '';
  const sep = (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ';' : ',';
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

/**
 * @param {ArrayBuffer|Uint8Array} data
 * @param {string} fileName - quyết định đọc kiểu nào (.xlsx / .csv)
 * @returns {Promise<string[][]>} các dòng (dòng trống giữa chừng giữ nguyên là mảng rỗng)
 */
export async function readSpreadsheet(data, fileName) {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  const lower = String(fileName || '').toLowerCase();
  if (lower.endsWith('.csv')) return parseCsv(td.decode(bytes));
  if (lower.endsWith('.xlsx')) return readXlsx(bytes);
  throw new Error('Chỉ nhận file .xlsx hoặc .csv');
}

// ------------------------------------------------------------------- cột
const norm = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Tiêu đề cột được nhận cho từng trường — so không dấu, không phân biệt hoa thường. */
export const MIDDLEMAN_COLUMNS = {
  code: ['ma', 'ma mm', 'ma mm fo', 'code'],
  name: ['ten', 'ten mm', 'ten mm fo', 'name'],
  type: ['loai', 'loai mm', 'vai tro', 'type'],
  province: ['tinh', 'tinh thanh', 'province'],
  commune: ['xa', 'xa phuong', 'phuong xa', 'commune'],
  address: ['dia chi', 'dia chi chi tiet', 'thon', 'address'],
};

/**
 * Dòng tiêu đề (dòng đầu không trống) → vị trí từng trường; các dòng sau → bản ghi. Thứ tự cột
 * không quan trọng — file của IDH chỉ cần đổi tên tiêu đề cho khớp. Bỏ dòng trống hoàn toàn;
 * `row` là số dòng trong Excel (1-based) để báo lỗi đúng chỗ người dùng nhìn thấy.
 */
export function rowsToMiddlemen(rows) {
  const headerAt = rows.findIndex((r) => r.some((c) => String(c).trim() !== ''));
  if (headerAt < 0) return { records: [], missing: Object.keys(MIDDLEMAN_COLUMNS) };
  const header = rows[headerAt].map(norm);
  const col = {};
  for (const [field, names] of Object.entries(MIDDLEMAN_COLUMNS)) {
    const i = header.findIndex((h) => names.includes(h));
    if (i >= 0) col[field] = i;
  }
  const missing = ['code', 'name', 'type', 'commune'].filter((f) => col[f] === undefined);
  const records = [];
  for (let r = headerAt + 1; r < rows.length; r++) {
    const cells = rows[r] ?? [];
    if (!cells.some((c) => String(c).trim() !== '')) continue;
    const rec = { row: r + 1 };
    for (const [field, i] of Object.entries(col)) rec[field] = String(cells[i] ?? '').trim();
    records.push(rec);
  }
  return { records, missing };
}
